import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EstadoBadge from "@/components/salud/EstadoBadge";
import { fmtTimestamp } from "@/lib/format";
import {
  Activity, Router, Cpu, ShieldAlert, ShieldCheck, ShieldOff, CheckCircle2, Clock, RefreshCw, MapPin, HelpCircle, DatabaseBackup,
} from "lucide-react";

const CAP_LABEL = { caida: "caída", panico: "botón de ayuda", presencia: "presencia", audio: "audio", cama: "cama" };
const ORIGEN_LABEL = { explicito: "explícito", cama: "cama", zona: "zona" };
const COB_META = {
  perdida: { label: "Protección perdida", cls: "border-[#9B2C2C]/40 bg-[#9B2C2C]/10 text-[#9B2C2C] dark:text-[#F2B8B8]", Icon: ShieldOff },
  no_confirmable: { label: "No confirmable", cls: "border-indigo-300 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300", Icon: HelpCircle },
  protegida: { label: "Protegida", cls: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300", Icon: ShieldCheck },
  sin_cobertura: { label: "Sin cobertura", cls: "border-slate-200 bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400", Icon: Cpu },
};

function hace(seg) {
  if (seg == null) return "sin registro";
  if (seg < 60) return `hace ${seg} s`;
  if (seg < 3600) return `hace ${Math.floor(seg / 60)} min`;
  if (seg < 86400) return `hace ${(seg / 3600).toFixed(1)} h`;
  return `hace ${(seg / 86400).toFixed(1)} días`;
}

function fmtDur(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

// Superficie técnica READ-ONLY del respaldo. Reutiliza el lenguaje visual de Health.
const RESP_META = {
  CONFIRMADO_OPERATIVO: {
    title: "Respaldo confirmado",
    cls: "border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800",
    iconCls: "text-emerald-600 dark:text-emerald-400",
    badge: "confirmado_operativo",
  },
  CONFIRMADO_CAIDO: {
    title: "Respaldo fuera del objetivo",
    cls: "border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800",
    iconCls: "text-amber-600 dark:text-amber-400",
    badge: "advertencia",
  },
  NO_CONFIRMABLE: {
    title: "Estado del respaldo no confirmable",
    cls: "border-indigo-200 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-800",
    iconCls: "text-indigo-600 dark:text-indigo-400",
    badge: "no_confirmable",
  },
};

function Campo({ k, v, testId }) {
  return (
    <div>
      <dt className="text-slate-400 dark:text-slate-500">{k}</dt>
      <dd data-testid={testId} className="font-medium text-slate-600 dark:text-slate-300 tabular-nums">{v}</dd>
    </div>
  );
}

function RespaldoBlock({ b }) {
  // H5: si el endpoint no respondió (b === null) NO se fabrica un OK: se muestra no confirmable.
  const estado = b?.estado || "NO_CONFIRMABLE";
  const m = RESP_META[estado] || RESP_META.NO_CONFIRMABLE;
  const edadTxt = b?.edad_min != null ? `hace ${b.edad_min} min` : "sin registro";
  return (
    <section data-testid="respaldo-block" className={`rounded-2xl border p-5 ${m.cls}`}>
      <div className="flex items-start gap-3">
        <DatabaseBackup strokeWidth={1.9} className={`h-7 w-7 shrink-0 ${m.iconCls}`} />
        <div className="min-w-0 w-full">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#012472] dark:text-[#80B3FF]">
              Respaldo de datos
            </h3>
            <EstadoBadge estado={m.badge} testId="respaldo-estado" size="sm" />
          </div>
          <p data-testid="respaldo-titulo" className="mt-1 text-lg font-semibold text-[#012472] dark:text-slate-100">
            {m.title}
          </p>
          {b == null ? (
            <p data-testid="respaldo-no-consultable" className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              No se pudo consultar el estado del respaldo.
            </p>
          ) : (
            <>
              {estado !== "NO_CONFIRMABLE" && (
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Último respaldo válido <span className="font-medium">{edadTxt}</span>.
                </p>
              )}
              <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-3">
                <Campo k="Último respaldo válido" v={b.ultimo_exito_en ? fmtTimestamp(b.ultimo_exito_en) : "sin registro"} />
                <Campo k="Edad" v={edadTxt} />
                <Campo k="Último intento" v={b.ultimo_intento_en ? fmtTimestamp(b.ultimo_intento_en) : "sin registro"} />
                <Campo k="Resultado del último intento" v={b.ultimo_intento_outcome || "—"} />
                <Campo k="Duración" v={fmtDur(b.duracion_ms)} />
                <Campo k="Destino" v={b.destino || "Object Store Galarya"} />
                {/* v2.11.7: la solicitud del disparador se distingue del resultado del respaldo. */}
                <Campo testId="respaldo-ultima-solicitud" k="Última solicitud" v={b.ultima_solicitud_en ? fmtTimestamp(b.ultima_solicitud_en) : "sin registro"} />
                <Campo testId="respaldo-solicitud-estado" k="Estado de la solicitud" v={b.solicitud_texto || "—"} />
              </dl>
              {b.aviso && (
                <p data-testid="respaldo-aviso" className="mt-3 rounded-lg bg-white/70 dark:bg-slate-900/40 px-3 py-2 text-sm font-medium text-amber-700 dark:text-amber-300">
                  {b.aviso}
                </p>
              )}
              {/* v2.11.3: causa sanitizada del último intento FAILED. Nunca se muestra en SUCCESS. */}
              {b.ultimo_intento_outcome === "FAILED" && (b.ultimo_error_class || b.ultimo_error_message) && (
                <div data-testid="respaldo-motivo-fallo" className="mt-3 rounded-lg border border-slate-200/80 bg-white/70 px-3 py-2 dark:border-slate-700/60 dark:bg-slate-900/40">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Motivo del último fallo
                  </p>
                  {b.ultimo_error_class && (
                    <p data-testid="respaldo-error-class" className="mt-0.5 text-sm font-medium text-slate-700 dark:text-slate-200">
                      {b.ultimo_error_class}
                    </p>
                  )}
                  {b.ultimo_error_message && (
                    <p data-testid="respaldo-error-message" className="mt-0.5 break-words text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                      {b.ultimo_error_message}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function VigilanteBlock({ v }) {
  if (!v) return null;
  const noConfiable = v.estado === "atrasado" || v.estado === "nunca_ejecutado";
  const map = {
    al_corriente: {
      title: "Evaluación del vigilante al corriente",
      cls: "border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800",
      Icon: CheckCircle2,
      iconCls: "text-emerald-600 dark:text-emerald-400",
    },
    atrasado: {
      title: "La evaluación del vigilante está ATRASADA",
      cls: "border-[#9B2C2C]/40 bg-[#9B2C2C]/10 dark:bg-[#9B2C2C]/25 dark:border-[#9B2C2C]/50",
      Icon: ShieldAlert,
      iconCls: "text-[#9B2C2C] dark:text-[#F2B8B8]",
    },
    nunca_ejecutado: {
      title: "El vigilante aún no completa ningún ciclo",
      cls: "border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800",
      Icon: Activity,
      iconCls: "text-amber-600 dark:text-amber-400",
    },
  };
  const c = map[v.estado] || map.nunca_ejecutado;
  const Icon = c.Icon;
  return (
    <section data-testid="vigilante-block" className={`rounded-2xl border p-5 ${c.cls}`}>
      <div className="flex items-start gap-3">
        <Icon strokeWidth={1.9} className={`h-7 w-7 shrink-0 ${c.iconCls}`} />
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-[#012472] dark:text-slate-100">{c.title}</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {v.estado === "nunca_ejecutado"
              ? "No hay ningún ciclo completo registrado (recién instalado o reiniciado)."
              : <>Último ciclo completo: <span className="font-medium">{fmtTimestamp(v.ultima_pasada)}</span> ({hace(v.atraso_seg)}).</>}
          </p>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Intervalo del vigilante: {v.intervalo_seg}s · margen: {v.margen_seg}s. Se refiere a cuándo se completó el último ciclo exitoso, no a si el proceso vive en este instante.
          </p>
          {noConfiable && (
            <p data-testid="vigilante-aviso" className="mt-3 rounded-lg bg-white/70 dark:bg-slate-900/40 px-3 py-2 text-sm font-medium text-[#9B2C2C] dark:text-[#F2B8B8]">
              ⚠ El estado técnico de puentes y sensores mostrado abajo puede no ser confiable hasta que la evaluación se ponga al corriente.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function SensorRow({ s }) {
  const transporte = s.usa_transporte_bearer
    ? "transporte propio (Bearer)"
    : (s.emite_heartbeat_propio ? "latido propio" : "vigilado por puente HM");
  return (
    <div data-testid={`sensor-${s.id}`} className="flex items-center justify-between gap-3 py-2.5 pl-4 border-t border-slate-100 dark:border-slate-700">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
          {s.identificador_evento} <span className="font-normal text-slate-400">· {s.tipo}</span>
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {s.zona_nombre || "Sin zona"} · {transporte}
          {s.ultimo_contacto_recibido ? ` · último contacto ${hace(s.antiguedad_seg)}` : ""}
        </p>
        {s.motivo && (
          <p data-testid={`sensor-motivo-${s.id}`} className="mt-0.5 text-xs italic text-slate-400 dark:text-slate-500">
            {s.motivo}
          </p>
        )}
      </div>
      <EstadoBadge estado={s.estado} testId={`sensor-estado-${s.id}`} size="sm" />
    </div>
  );
}

function CoberturaBlock({ cob }) {
  const navigate = useNavigate();
  if (!cob || !Array.isArray(cob.cobertura)) return null;
  const rows = cob.cobertura.filter((x) => x.dispositivos_total > 0 || x.incidente_abierto);
  if (rows.length === 0) return null;
  const orden = { perdida: 0, no_confirmable: 1, protegida: 2, sin_cobertura: 3 };
  rows.sort((a, b) => (orden[a.estado] ?? 9) - (orden[b.estado] ?? 9));
  const r = cob.resumen || {};
  return (
    <section data-testid="cobertura-block" className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[#012472] dark:text-[#80B3FF]">
          Cobertura crítica
        </h3>
        <div className="flex flex-wrap gap-2 text-xs">
          <Chip label="Perdidas" value={r.capacidad_perdida} tone="rojo" />
          <Chip label="No confirmables" value={r.no_confirmable} tone="indigo" />
          <Chip label="Incidentes abiertos" value={r.incidentes_abiertos} tone="indigo" />
        </div>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
        Protección por persona para capacidades críticas (caída/botón de ayuda). Una <strong>pérdida total</strong> sostenida genera una incidencia técnica ALTA en la consola.
      </p>
      <div className="space-y-1.5">
        {rows.map((x) => {
          const m = COB_META[x.estado] || COB_META.sin_cobertura;
          const Icon = m.Icon;
          const linkable = !!x.incidente_alarma_id;
          const goIncidencia = () => linkable && navigate(`/consola?alarma=${x.incidente_alarma_id}`);
          return (
            <div
              key={`${x.persona_id}-${x.capacidad}`}
              data-testid={`cobertura-${x.persona_id}-${x.capacidad}`}
              onClick={goIncidencia}
              role={linkable ? "button" : undefined}
              tabIndex={linkable ? 0 : undefined}
              onKeyDown={(e) => { if (linkable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); goIncidencia(); } }}
              className={`flex items-center justify-between gap-3 border-t border-slate-100 dark:border-slate-700 pt-2 ${
                linkable ? "cursor-pointer -mx-2 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors" : ""
              }`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                  {x.persona_nombre || "Persona"}{" "}
                  <span className="font-normal text-slate-400">· {CAP_LABEL[x.capacidad] || x.capacidad}</span>
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {x.sitio_nombre}
                  {x.dispositivos_total > 0
                    ? ` · ${x.dispositivos_operativos ?? (x.dispositivos_total - x.dispositivos_caidos)}/${x.dispositivos_total} sensores operativos`
                    : ""}
                  {x.origen_cobertura ? ` · por ${ORIGEN_LABEL[x.origen_cobertura] || x.origen_cobertura}` : ""}
                  {linkable ? " · ver incidencia →" : ""}
                </p>
              </div>
              <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${m.cls}`}>
                <Icon strokeWidth={1.8} className="h-3.5 w-3.5" /> {m.label}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function SaludTecnica() {
  const { isSupervisor } = useAuth();
  const [data, setData] = useState(null);
  const [cob, setCob] = useState(null);
  const [backup, setBackup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const [salud, cobertura, backupRes] = await Promise.all([
        api.get("/panel-salud"),
        api.get("/cobertura").catch(() => ({ data: null })),
        api.get("/ops/backup-health").catch(() => ({ data: null })),
      ]);
      setData(salud.data);
      setCob(cobertura.data);
      setBackup(backupRes.data);
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isSupervisor) { setLoading(false); return; }
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load, isSupervisor]);

  if (!isSupervisor) {
    return (
      <div data-testid="salud-denegado" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
        Esta pantalla es solo para supervisores.
      </div>
    );
  }

  if (loading) return <p className="text-slate-400">Cargando salud técnica…</p>;
  if (error || !data) return <p data-testid="salud-error" className="text-[#9B2C2C]">No se pudo consultar el panel de salud técnica.</p>;

  const sensoresPorPuente = {};
  for (const s of data.sensores) {
    const key = s.puente_id || "__sin_puente__";
    (sensoresPorPuente[key] = sensoresPorPuente[key] || []).push(s);
  }
  const r = data.resumen || {};

  return (
    <div className="space-y-6" data-testid="salud-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#012472] dark:text-slate-100">Salud técnica</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Estado de puentes y sensores calculado con la hora del servidor. Solo lectura.
          </p>
        </div>
        <button onClick={load} data-testid="btn-refrescar-salud" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-600 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
          <RefreshCw strokeWidth={1.7} className="h-4 w-4" /> Actualizar
        </button>
      </div>

      <VigilanteBlock v={data.vigilante} />

      <RespaldoBlock b={backup} />

      <CoberturaBlock cob={cob} />

      {/* Resumen */}
      <div className="flex flex-wrap gap-2 text-sm" data-testid="salud-resumen">
        <Chip label="Puentes silenciosos" value={r.puentes_silenciosos} tone="rojo" />
        <Chip label="Puentes en advertencia" value={r.puentes_advertencia} tone="ambar" />
        <Chip label="Puentes no confirmables" value={r.puentes_no_confirmable} tone="indigo" />
        <Chip label="Sensores operativos" value={r.sensores_confirmado_operativo} tone="verde" />
        <Chip label="Sensores caídos" value={r.sensores_confirmado_caido} tone="rojo" />
        <Chip label="Sensores no confirmables" value={r.sensores_no_confirmable} tone="indigo" />
        <Chip label="Vigilancia nocturna apagada" value={r.vigilancia_cama_apagada} tone="ambar" />
      </div>

      {/* Vigilancia nocturna de cama: apagada != inexistente (visible, no silenciosa) */}
      {(data.vigilancia_cama || []).some((v) => v.personas_sin_vigilancia > 0) && (
        <section data-testid="vigilancia-cama" className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[#012472] dark:text-[#80B3FF] mb-2">
            Vigilancia nocturna de cama
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Sitios con personas cuya vigilancia nocturna de cama está <strong>no configurada</strong> (no se genera alarma de fuera de cama sin retorno).
          </p>
          <div className="space-y-1.5">
            {data.vigilancia_cama.filter((v) => v.personas_sin_vigilancia > 0).map((v) => (
              <div key={v.sitio_id} data-testid={`vigilancia-${v.sitio_id}`} className="flex items-center justify-between text-sm">
                <span className="text-slate-700 dark:text-slate-200">{v.sitio_nombre || "Sitio"}</span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  {v.personas_sin_vigilancia} sin vigilancia nocturna
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Puentes + sensores anidados */}
      <div className="space-y-4">
        {data.puentes.length === 0 && (
          <p className="text-slate-400">No hay puentes activos.</p>
        )}
        {data.puentes.map((p) => (
          <section key={p.id} data-testid={`puente-${p.id}`} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
            <div className="flex items-center justify-between gap-3 p-5">
              <div className="flex items-center gap-3 min-w-0">
                <Router strokeWidth={1.8} className="h-6 w-6 text-[#012472] dark:text-[#80B3FF] shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-[#012472] dark:text-slate-100 truncate">{p.nombre}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                    <MapPin strokeWidth={1.5} className="h-3.5 w-3.5" /> {p.sitio_nombre}
                    {" · "}latido {hace(p.antiguedad_seg)}
                    {" · "}tolerancia {p.tolerancia_silencio_seg}s
                    {p.modo_liveness ? ` · liveness ${p.modo_liveness === "periodico" ? "periódico" : "por evento"}` : ""}
                    {p.eventos_rechazados > 0 && <span className="text-[#9B2C2C]"> · {p.eventos_rechazados} evento(s) rechazado(s)</span>}
                  </p>
                  {p.advertencia_config && (
                    <p data-testid={`puente-config-${p.id}`} className="mt-0.5 text-xs font-medium text-indigo-600 dark:text-indigo-300">
                      Configuración pendiente: declara el modo de liveness del puente (periódico o por evento).
                    </p>
                  )}
                </div>
              </div>
              <EstadoBadge estado={p.estado} testId={`puente-estado-${p.id}`} />
            </div>
            <div className="bg-slate-50/60 dark:bg-slate-900/30 px-3 pb-1">
              {(sensoresPorPuente[p.id] || []).length === 0 ? (
                <p className="py-3 pl-4 text-xs text-slate-400">Sin sensores asignados.</p>
              ) : (
                sensoresPorPuente[p.id].map((s) => <SensorRow key={s.id} s={s} />)
              )}
            </div>
          </section>
        ))}

        {sensoresPorPuente["__sin_puente__"] && (
          <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
            <div className="flex items-center gap-3 p-5">
              <Cpu strokeWidth={1.8} className="h-6 w-6 text-slate-400 shrink-0" />
              <p className="font-semibold text-slate-600 dark:text-slate-200">Sensores sin puente asignado</p>
            </div>
            <div className="bg-slate-50/60 dark:bg-slate-900/30 px-3 pb-1">
              {sensoresPorPuente["__sin_puente__"].map((s) => <SensorRow key={s.id} s={s} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Chip({ label, value, tone }) {
  const tones = {
    rojo: "bg-[#9B2C2C]/10 text-[#9B2C2C] border-[#9B2C2C]/30",
    ambar: "bg-amber-50 text-amber-800 border-amber-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    verde: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 ${tones[tone] || tones.indigo}`}>
      <span className="tabular-nums font-semibold">{value ?? 0}</span> {label}
    </span>
  );
}
